import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { Textarea } from '../components/ui/Textarea';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { Plus, Filter, Download, Eye, Edit, Search, Receipt, AlertTriangle, CheckCircle2, Clock, X, Save, Mail, Calendar, LayoutGrid, List as ListIcon, Euro } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// Kanban columns with colors matching Contracts page style
const kanbanColumns = [{
  id: 'da_pianificare',
  title: 'Da pianificare',
  color: 'bg-slate-50 border-slate-200'
}, {
  id: 'pianificato',
  title: 'Pianificato',
  color: 'bg-blue-50 border-blue-200'
}, {
  id: 'proforma',
  title: 'Proforma',
  color: 'bg-yellow-50 border-yellow-200'
}, {
  id: 'fatturato',
  title: 'Fatturato',
  color: 'bg-indigo-50 border-indigo-200'
}, {
  id: 'pagato',
  title: 'Pagato',
  color: 'bg-green-50 border-green-200'
}, {
  id: 'sollecitare',
  title: 'Sollecitare',
  color: 'bg-orange-50 border-orange-200'
}, {
  id: 'archiviato',
  title: 'Archiviato',
  color: 'bg-slate-100 border-slate-300'
}];
// Mock data with new statuses
const initialInvoicesData = Array.from({
  length: 50
}, (_, i) => ({
  id: i + 1,
  code: `${i % 2 === 0 ? 'F' : 'P'}-2025/${String(i + 1).padStart(3, '0')}`,
  type: i % 2 === 0 ? 'Fattura' : 'Proforma',
  client: `Azienda ${i} SRL`,
  date: '31/01/2025',
  amount: `€ ${1200 + i * 50}`,
  status: ['da_pianificare', 'pianificato', 'proforma', 'fatturato', 'pagato', 'sollecitare', 'archiviato'][i % 7],
  dueDate: '28/02/2025',
  approval: i % 4 === 0 ? 'pending' : 'approved',
  reminderCount: i % 7 === 5 ? Math.floor(Math.random() * 4) : 0,
  isRecurring: i % 5 === 0,
  recurringDates: i % 5 === 0 ? ['2025-01-15', '2025-04-15', '2025-07-15', '2025-10-15'] : []
}));
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'type',
  label: 'Tipo'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'date',
  label: 'Data emiss.'
}, {
  key: 'amount',
  label: 'Importo'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'approval',
  label: 'Avallo'
}, {
  key: 'dueDate',
  label: 'Scadenza'
}, {
  key: 'actions',
  label: 'Azioni'
}];
const statusLabels: Record<string, string> = {
  da_pianificare: 'Da pianificare',
  pianificato: 'Pianificato',
  proforma: 'Proforma',
  fatturato: 'Fatturato',
  pagato: 'Pagato',
  sollecitare: 'Sollecitare',
  archiviato: 'Archiviato'
};
const statusColors: Record<string, string> = {
  da_pianificare: 'neutral',
  pianificato: 'info',
  proforma: 'warning',
  fatturato: 'primary',
  pagato: 'success',
  sollecitare: 'danger',
  archiviato: 'neutral'
};
export function InvoicingPage() {
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [invoicesData, setInvoicesData] = useState(initialInvoicesData);
  const [draggedCard, setDraggedCard] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(invoicesData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['code', 'client']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  // Auto-archive after 3 reminders
  useEffect(() => {
    setInvoicesData(prev => prev.map(invoice => {
      if (invoice.status === 'sollecitare' && invoice.reminderCount >= 3) {
        return {
          ...invoice,
          status: 'archiviato'
        };
      }
      return invoice;
    }));
  }, []);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({
      isRecurring: false,
      recurringDates: [],
      sendEmailOnExpiry: true
    });
    setIsModalOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, card: any) => {
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (draggedCard && draggedCard.status !== targetColumnId) {
      setInvoicesData(prev => prev.map(item => item.id === draggedCard.id ? {
        ...item,
        status: targetColumnId
      } : item));
    }
    setDraggedCard(null);
  };
  const getCardsByColumn = (columnId: string) => {
    return searchResult.filter(c => c.status === columnId);
  };
  return <Layout data-id="element-2118">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-2119">
        <div data-id="element-2120">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2121">
            Fatturazione e pagamenti
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-2122">
            Gestione ciclo attivo, avalli e recupero crediti.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-2123">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-2124">
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2125">
              <ListIcon className="h-4 w-4 inline-block mr-1.5" data-id="element-2126" /> Lista
            </button>
            <button onClick={() => setViewMode('kanban')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'kanban' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2127">
              <LayoutGrid className="h-4 w-4 inline-block mr-1.5" data-id="element-2128" /> Kanban
            </button>
          </div>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-2130" />} data-id="element-2129">
            Esporta elenco
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-2132" />} data-id="element-2131">
            Nuovo documento
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-2133">
        <div className="p-4 flex flex-col gap-4" data-id="element-2134">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-2135">
            <div className="flex-1" data-id="element-2136">
              <Input placeholder="Cerca documento, cliente..." startContent={<Search className="h-4 w-4" data-id="element-2138" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-2137" />
            </div>
            <div className="flex gap-2" data-id="element-2139">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-2141" />} data-id="element-2140">
                Filtri{' '}
                {Object.keys(filters).filter(k => filters[k]).length > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-2142">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              {viewMode === 'list' && <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="invoicing" data-id="element-2143" />}
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-2144">
              <Select label="Tipo" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Fattura',
            label: 'Fattura'
          }, {
            value: 'Proforma',
            label: 'Proforma'
          }]} data-id="element-2145" />
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'da_pianificare',
            label: 'Da pianificare'
          }, {
            value: 'pianificato',
            label: 'Pianificato'
          }, {
            value: 'proforma',
            label: 'Proforma'
          }, {
            value: 'fatturato',
            label: 'Fatturato'
          }, {
            value: 'pagato',
            label: 'Pagato'
          }, {
            value: 'sollecitare',
            label: 'Sollecitare'
          }, {
            value: 'archiviato',
            label: 'Archiviato'
          }]} data-id="element-2146" />
              <div className="col-span-full flex justify-end" data-id="element-2147">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-2149" />} data-id="element-2148">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      {viewMode === 'list' ? <Card noPadding className="overflow-hidden" data-id="element-2150">
          <div className="overflow-x-auto" data-id="element-2151">
            <table className="w-full text-left border-collapse table-fixed" data-id="element-2152">
              <thead data-id="element-2153">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2154">
                  {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-right w-28' : ''}`} data-id="element-2155">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2156">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2157">
                    {visibleColumns.includes('code') && <td className="px-6 py-4 font-mono text-xs text-slate-500" data-id="element-2158">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-6 py-4" data-id="element-2159">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${item.type === 'Fattura' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-700'}`} data-id="element-2160">
                          {item.type}
                        </span>
                      </td>}
                    {visibleColumns.includes('client') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2161">
                        {item.client}
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2162">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('amount') && <td className="px-6 py-4 text-sm font-mono text-slate-900" data-id="element-2163">
                        {item.amount}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-6 py-4" data-id="element-2164">
                        <Badge variant={statusColors[item.status] as any || 'neutral'} data-id="element-2165">
                          {statusLabels[item.status] || item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('approval') && <td className="px-6 py-4" data-id="element-2166">
                        {item.approval === 'pending' && <Badge variant="warning" size="sm" data-id="element-2167">
                            Da avallare
                          </Badge>}
                        {item.approval === 'approved' && <Badge variant="success" size="sm" data-id="element-2168">
                            Avallato
                          </Badge>}
                      </td>}
                    {visibleColumns.includes('dueDate') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2169">
                        {item.dueDate}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-6 py-4 text-right" data-id="element-2170">
                        <div className="flex items-center justify-end gap-2" data-id="element-2171">
                          <Button variant="light" size="sm" className="!px-2" onClick={() => handleView(item)} data-id="element-2172">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-2173" />
                          </Button>
                          <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-2174">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-2175" />
                          </Button>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2176">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2177" />
          </div>
        </Card> :
    // Kanban View with colors matching Contracts page style
    <div className="overflow-x-auto pb-4" data-id="element-2178">
          <div className="flex gap-4 min-w-max" data-id="element-2179">
            {kanbanColumns.map(column => {
          const cards = getCardsByColumn(column.id);
          return <div key={column.id} className={`w-72 flex-shrink-0 rounded-xl border ${column.color} flex flex-col max-h-[calc(100vh-280px)]`} onDragOver={handleDragOver} onDrop={e => handleDrop(e, column.id)} data-id="element-2180">
                  <div className="p-3 border-b border-slate-200/50 flex items-center justify-between" data-id="element-2181">
                    <h3 className="font-semibold text-slate-700 text-sm" data-id="element-2182">
                      {column.title}
                    </h3>
                    <Badge variant="neutral" size="sm" data-id="element-2183">
                      {cards.length}
                    </Badge>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2" data-id="element-2184">
                    {cards.map(card => <div key={card.id} draggable onDragStart={e => handleDragStart(e, card)} onClick={() => handleView(card)} className={`bg-white rounded-lg p-3 shadow-sm border border-slate-100 cursor-pointer hover:shadow-md transition-all ${draggedCard?.id === card.id ? 'opacity-50' : ''}`} data-id="element-2185">
                        <div className="flex items-start justify-between mb-2" data-id="element-2186">
                          <span className="text-[10px] font-mono text-slate-400" data-id="element-2187">
                            {card.code}
                          </span>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${card.type === 'Fattura' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-2188">
                            {card.type}
                          </span>
                        </div>
                        <h4 className="font-semibold text-slate-900 text-sm mb-1 line-clamp-1" data-id="element-2189">
                          {card.client}
                        </h4>
                        <div className="flex items-center justify-between mt-2" data-id="element-2190">
                          <span className="text-sm font-mono font-medium text-seform-navy" data-id="element-2191">
                            {card.amount}
                          </span>
                          <span className="text-xs text-slate-400" data-id="element-2192">
                            {card.dueDate}
                          </span>
                        </div>
                        {card.status === 'sollecitare' && card.reminderCount > 0 && <div className="mt-2 flex items-center gap-1 text-xs text-orange-600" data-id="element-2193">
                              <AlertTriangle className="h-3 w-3" data-id="element-2194" />
                              {card.reminderCount} sollecit
                              {card.reminderCount === 1 ? 'o' : 'i'}
                              {card.reminderCount >= 3 && <span className="text-red-600 font-medium" data-id="element-2195">
                                  {' '}
                                  → Archiviazione
                                </span>}
                            </div>}
                        {card.isRecurring && <div className="mt-2 flex items-center gap-1 text-xs text-blue-600" data-id="element-2196">
                            <Calendar className="h-3 w-3" data-id="element-2197" />
                            Ricorrente
                          </div>}
                      </div>)}
                    {cards.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-2198">
                        Nessun elemento
                      </div>}
                  </div>
                </div>;
        })}
          </div>
        </div>}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio documento" size="lg" data-id="element-2199">
        {selectedItem && <div className="space-y-6" data-id="element-2200">
            <div className="grid grid-cols-2 gap-4" data-id="element-2201">
              <div data-id="element-2202">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2203">
                  Codice
                </span>
                <p className="font-mono font-medium" data-id="element-2204">{selectedItem.code}</p>
              </div>
              <div data-id="element-2205">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2206">
                  Tipo
                </span>
                <p className="font-medium" data-id="element-2207">{selectedItem.type}</p>
              </div>
              <div data-id="element-2208">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2209">
                  Cliente
                </span>
                <p className="font-medium" data-id="element-2210">{selectedItem.client}</p>
              </div>
              <div data-id="element-2211">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2212">
                  Stato
                </span>
                <div className="mt-1" data-id="element-2213">
                  <Badge variant={statusColors[selectedItem.status] as any || 'neutral'} data-id="element-2214">
                    {statusLabels[selectedItem.status] || selectedItem.status}
                  </Badge>
                </div>
              </div>
              <div data-id="element-2215">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2216">
                  Importo
                </span>
                <p className="font-mono font-bold text-seform-navy" data-id="element-2217">
                  {selectedItem.amount}
                </p>
              </div>
              <div data-id="element-2218">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2219">
                  Scadenza
                </span>
                <p className="font-medium" data-id="element-2220">{selectedItem.dueDate}</p>
              </div>
            </div>
            {selectedItem.isRecurring && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-2221">
                <div className="flex items-center gap-2 text-blue-700 font-medium mb-2" data-id="element-2222">
                  <Calendar className="h-4 w-4" data-id="element-2223" />
                  Pagamento ricorrente
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-2224">
                  {selectedItem.recurringDates?.map((date: string, i: number) => <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded" data-id="element-2225">
                        {date}
                      </span>)}
                </div>
              </div>}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2226">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-2227">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-2229" />} data-id="element-2228">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica documento' : 'Nuovo documento'} size="2xl" data-id="element-2230">
        <AnimatePresence mode="wait" data-id="element-2231">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2232">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2233">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2234" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2235">
                Documento salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2236">
              <div className="space-y-8" data-id="element-2237">
                <section data-id="element-2238">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2239">
                    <Receipt className="h-4 w-4" data-id="element-2240" /> Dati documento
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2241">
                    <ProgressiveNumberInput type="invoice" value={formData.code} onChange={val => setFormData({
                  ...formData,
                  code: val
                })} disabled={!!selectedItem} label="Codice" data-id="element-2242" />
                    <Select label="Tipo documento" value={formData.type || 'Fattura'} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Fattura',
                  label: 'Fattura'
                }, {
                  value: 'Proforma',
                  label: 'Proforma'
                }]} required data-id="element-2243" />
                    <Select label="Cliente" value={formData.client || ''} onChange={e => setFormData({
                  ...formData,
                  client: e.target.value
                })} options={[{
                  value: 'Azienda 1 SRL',
                  label: 'Azienda 1 SRL'
                }, {
                  value: 'Azienda 2 SRL',
                  label: 'Azienda 2 SRL'
                }]} required data-id="element-2244" />
                    <Select label="Stato" value={formData.status || 'da_pianificare'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'da_pianificare',
                  label: 'Da pianificare'
                }, {
                  value: 'pianificato',
                  label: 'Pianificato'
                }, {
                  value: 'proforma',
                  label: 'Proforma'
                }, {
                  value: 'fatturato',
                  label: 'Fatturato'
                }, {
                  value: 'pagato',
                  label: 'Pagato'
                }, {
                  value: 'sollecitare',
                  label: 'Sollecitare'
                }, {
                  value: 'archiviato',
                  label: 'Archiviato'
                }]} required data-id="element-2245" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-2246"></div>

                <section data-id="element-2247">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2248">
                    <Euro className="h-4 w-4" data-id="element-2249" /> Dettagli economici
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2250">
                    <Input label="Importo (€)" type="number" placeholder="0.00" value={formData.amount?.replace('€ ', '') || ''} onChange={e => setFormData({
                  ...formData,
                  amount: `€ ${e.target.value}`
                })} required data-id="element-2251" />
                    <Input label="Data emissione" type="date" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-2252" />
                    <Input label="Data scadenza" type="date" value={formData.dueDate || ''} onChange={e => setFormData({
                  ...formData,
                  dueDate: e.target.value
                })} data-id="element-2253" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-2254"></div>

                <section data-id="element-2255">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2256">
                    <Calendar className="h-4 w-4" data-id="element-2257" /> Pagamento ricorrente
                  </h4>
                  <div className="space-y-4" data-id="element-2258">
                    <div className="flex items-center gap-2" data-id="element-2259">
                      <input type="checkbox" id="isRecurring" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.isRecurring || false} onChange={e => setFormData({
                    ...formData,
                    isRecurring: e.target.checked
                  })} data-id="element-2260" />
                      <label htmlFor="isRecurring" className="text-sm text-slate-700" data-id="element-2261">
                        Abilita pagamento ricorrente
                      </label>
                    </div>
                    {formData.isRecurring && <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-2262">
                        <p className="text-sm text-slate-600 mb-3" data-id="element-2263">
                          Seleziona le date di pagamento:
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-2264">
                          {['2025-01-15', '2025-04-15', '2025-07-15', '2025-10-15'].map(date => <div key={date} className="flex items-center gap-2" data-id="element-2265">
                              <input type="checkbox" id={`date-${date}`} className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.recurringDates?.includes(date) || false} onChange={e => {
                        const dates = formData.recurringDates || [];
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            recurringDates: [...dates, date]
                          });
                        } else {
                          setFormData({
                            ...formData,
                            recurringDates: dates.filter((d: string) => d !== date)
                          });
                        }
                      }} data-id="element-2266" />
                              <label htmlFor={`date-${date}`} className="text-sm text-slate-600" data-id="element-2267">
                                {date}
                              </label>
                            </div>)}
                        </div>
                      </div>}
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-2268"></div>

                <section data-id="element-2269">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2270">
                    <Mail className="h-4 w-4" data-id="element-2271" /> Notifiche automatiche
                  </h4>
                  <div className="space-y-3" data-id="element-2272">
                    <div className="flex items-center gap-2" data-id="element-2273">
                      <input type="checkbox" id="sendEmailOnExpiry" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.sendEmailOnExpiry !== false} onChange={e => setFormData({
                    ...formData,
                    sendEmailOnExpiry: e.target.checked
                  })} data-id="element-2274" />
                      <label htmlFor="sendEmailOnExpiry" className="text-sm text-slate-700" data-id="element-2275">
                        Invia email automatica alla scadenza al cliente
                      </label>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2276">
                      <input type="checkbox" id="sendEmailToSEF" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.sendEmailToSEF !== false} onChange={e => setFormData({
                    ...formData,
                    sendEmailToSEF: e.target.checked
                  })} data-id="element-2277" />
                      <label htmlFor="sendEmailToSEF" className="text-sm text-slate-700" data-id="element-2278">
                        Invia notifica a SEF alla scadenza
                      </label>
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2279">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-2280">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2282" />} data-id="element-2281">
                    Salva documento
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}